/**
 * Where an old `/projects?...` URL goes now.
 *
 * The drill-down encoded its position in query params — project, then category,
 * then topic — and those URLs are in bookmarks, in browser history, and in
 * links this app generates for itself. Retiring the screen without mapping them
 * would turn every one into a dead end.
 *
 * The node param keeps the `kind-id` spelling the workspace already parses, so
 * a legacy CATEGORY id stays addressable as `category-N` and does not have to
 * be resolved to a topic id here.
 *
 * Its own dependency-free module: the component that uses it imports
 * react-router, which CRA's jest cannot resolve.
 */
export function subjectPathFor(params: URLSearchParams): string {
  const project = params.get('project');
  // `create=true` opened the new-subject modal on arrival. Dropping it would
  // leave the "+ New Subject" links in the workspace breadcrumb landing on a
  // list with nothing open — working, but silently one step short.
  const create = params.get('create') === 'true';
  if (!project) return create ? '/subjects?create=true' : '/subjects';

  const topic = params.get('topic');
  const category = params.get('category');
  const node = topic ? `topic-${topic}` : category ? `category-${category}` : null;

  // The drill-down's deepest level was the keyword workspace, which is the
  // Evidence tab here. Anything shallower has no tab of its own — it was a list
  // of children, and the rail is that list now.
  const tab = topic ? 'evidence' : null;

  const qs = new URLSearchParams();
  if (tab) qs.set('tab', tab);
  if (node) qs.set('node', node);
  // The keywords pane reads this to scroll to and flash one row. Dropping it
  // turned "see this keyword in its subject" into "here is the subject" — the
  // link still worked, so nothing looked broken.
  const highlight = params.get('highlight_keyword');
  if (highlight && node) qs.set('highlight_keyword', highlight);
  const suffix = qs.toString();
  return `/subjects/${project}${suffix ? `?${suffix}` : ''}`;
}
